import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  Ref,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Studio3DXViewer } from "@/vendor/studio3dx/studio3dx-viewer-api-1.2.0";

interface StudioContextValue {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  config: any;
  setConfig: any;
  viewer: any;
  loaded: boolean;
  addTexture: (key: string, dataUrl: string) => void;
  getTexture: (key: string) => string | undefined;
}

// Assuming viewer.ts is a class or object, you could replace it with the correct type
// @ts-ignore
const StudioContext = createContext<StudioContextValue>({});

interface StudioProviderProps {
  children: React.ReactNode;
}

export const StudioProvider: FunctionComponent<
  PropsWithChildren<StudioProviderProps>
> = (props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [config, setConfig] = useState<any>();
  const [viewer, setViewer] = useState<any>();

  const [textureCache, _] = useState(new Map<string, string>());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!viewer && iframeRef.current && config) {
      // By default, the latest version of the viewer API will be used.
      const viewer = new Studio3DXViewer(iframeRef.current) as any;

      viewer.addEventListener("viewerready", () => {
        setLoaded(true);
      });

      viewer.init({
        modelID: config.modelId,
        baseUrl: config.baseUrl,
        autoPlay: true,
      });

      setViewer(viewer);
    }
  }, [config, viewer, iframeRef]);

  const addTexture = (key: string, dataUrl: string) => {
    viewer.addTexture(dataUrl, (textureId: string) => {
      textureCache.set(key, textureId);
    });
  };

  const getTexture = (key: string) => {
    return textureCache.get(key);
  };

  return (
    <StudioContext.Provider
      value={{
        viewer,
        loaded,
        iframeRef: iframeRef as React.RefObject<HTMLIFrameElement>,
        config,
        setConfig,
        getTexture,
        addTexture,
      }}
    >
      {props.children}
    </StudioContext.Provider>
  );
};

export const useStudio = (config?: any) => {
  const context = useContext(StudioContext);
  if (!context) {
    throw new Error("useStudio must be used within a StudioProvider");
  }

  useEffect(() => {
    if (config) context.setConfig(config);
  }, []);

  return context;
};
